import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../context/AuthContext';

export default function Login() {
    const navigate = useNavigate();
    const { request } = useApi();
    const { login } = useAuth();
    const [isLoaded, setIsLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [clickAnimation, setClickAnimation] = useState(false);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setClickAnimation(true); // Animasyonu başlat

        if (!email || !password) {
            setError('E-posta ve şifre boş olamaz.');
            setIsSubmitting(false);
            return;
        }

        try {
            const res = await request('/auth/login', 'POST', { email, password });
            console.log("🧪 Login Cevap:", res);

            const token = res?.data?.token;

            if (!token) {
                setError('Token alınamadı.');
                setIsSubmitting(false);
                return;
            }

            login(token);

            const { role, id } = jwtDecode(token);
            localStorage.setItem('auth', 'true');
            localStorage.setItem('role', role);

            if (role === 'admin') {
                navigate('/admin-panel');
            } else if (role === 'owner') {
                localStorage.setItem('ownerId', id);
                navigate(`/owner-panel/${id}`);
            } else {
                localStorage.setItem('userId', id);
                navigate(`/profile/${id}`);
            }

        } catch (err) {
            setError(err.message || 'Giriş başarısız.');
            setIsSubmitting(false);
        } finally {
            // Animasyonun tamamlanması için küçük bir gecikme
            setTimeout(() => setClickAnimation(false), 1000);
        }
    };

    return (
        <section className="relative w-full min-h-screen bg-gradient-to-br from-[#241c15] via-[#523323] to-[#7F6B52] flex items-center justify-center overflow-hidden">
            {/* Arkaplan animasyonları */}
            <div className="absolute inset-0 overflow-hidden">
                <div className={`absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-[#F9D923]/20 blur-xl animate-float-1 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}></div>
                <div className={`absolute top-1/3 right-1/4 w-40 h-40 rounded-full bg-[#FF914D]/20 blur-xl animate-float-2 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}></div>
                <div className={`absolute bottom-1/4 left-1/3 w-36 h-36 rounded-full bg-[#7F6B52]/20 blur-xl animate-float-3 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}></div>
            </div>

            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-[#241c15] via-[#523323] to-[#7F6B52] opacity-80" />

            <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-10 w-full">
                <form
                    onSubmit={handleSubmit}
                    className={`bg-white/20 backdrop-blur-2xl rounded-3xl shadow-2xl max-w-md w-full p-10 border border-[#F9D923]/30 flex flex-col items-center transition-all duration-1000 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
                >
                    <h2 className={`text-3xl font-extrabold mb-8 text-center bg-gradient-to-r from-[#F9D923] via-[#FF914D] to-[#7F6B52] text-transparent bg-clip-text drop-shadow-lg ${clickAnimation ? 'animate-pulse' : ''}`}>
                        Giriş Yap
                    </h2>

                    {error && (
                        <div className={`w-full mb-4 overflow-hidden ${error ? 'animate-shake' : ''}`}>
                            <p className="text-red-500 text-sm text-center font-semibold bg-white/60 rounded py-2 px-4 border border-red-200 shadow">
                                {error}
                            </p>
                        </div>
                    )}

                    <div className="w-full space-y-4">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="E-posta"
                            className={`w-full px-4 py-3 border border-[#F9D923]/30 bg-white/40 rounded-xl text-[#241c15] font-medium placeholder-[#523323]/60 focus:outline-none focus:ring-2 focus:ring-[#F9D923] shadow-sm transition-all duration-300 hover:scale-[1.02] focus:scale-[1.02] ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                            style={{ transitionDelay: '0.1s' }}
                        />

                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Şifre"
                            className={`w-full px-4 py-3 border border-[#F9D923]/30 bg-white/40 rounded-xl text-[#241c15] font-medium placeholder-[#523323]/60 focus:outline-none focus:ring-2 focus:ring-[#F9D923] shadow-sm transition-all duration-300 hover:scale-[1.02] focus:scale-[1.02] ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                            style={{ transitionDelay: '0.2s' }}
                        />
                    </div>

                    <div className="w-full mt-6 text-center">
                        <p className="text-sm text-white/80">
                            Hesabınız yok mu?{' '}
                            <button
                                type="button"
                                onClick={() => navigate('/signup')}
                                className="text-[#F9D923] font-semibold hover:underline hover:text-[#FF914D] transition"
                            >
                                Kayıt Ol
                            </button>
                        </p>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full mt-6 py-3 rounded-xl font-bold text-lg bg-gradient-to-r from-[#F9D923] via-[#FF914D] to-[#7F6B52] text-white shadow-lg hover:brightness-110 transition-all duration-500 border-none relative overflow-hidden group ${isSubmitting ? 'opacity-80 cursor-not-allowed' : 'hover:scale-105'} ${clickAnimation ? 'animate-button-click' : ''}`}
                        onClick={() => setClickAnimation(true)}
                    >
                        <span className={`relative z-10 flex items-center justify-center ${isSubmitting ? 'opacity-0' : 'opacity-100'}`}>
                            Giriş Yap
                        </span>
                        {isSubmitting && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )}
                        <span className="absolute inset-0 bg-gradient-to-r from-[#F9D923]/30 via-[#FF914D]/30 to-[#7F6B52]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>






                        {/* Tıklama animasyonu için ekstra element */}
                        {clickAnimation && (
                            <span className="absolute inset-0 bg-white/30 rounded-xl animate-ping-once"></span>
                        )}
                    </button>
                </form>
            </div>

            {/* Tailwind'e özel animasyonlar */}
            <style jsx>{`
                @keyframes float-1 {
                    0%, 100% { transform: translate(0, 0) rotate(0deg); }
                    50% { transform: translate(20px, 20px) rotate(5deg); }
                }
                @keyframes float-2 {
                    0%, 100% { transform: translate(0, 0) rotate(0deg); }
                    50% { transform: translate(-15px, 15px) rotate(-5deg); }
                }
                @keyframes float-3 {
                    0%, 100% { transform: translate(0, 0) rotate(0deg); }
                    50% { transform: translate(10px, -10px) rotate(3deg); }
                }
                .animate-float-1 { animation: float-1 8s ease-in-out infinite; }
                .animate-float-2 { animation: float-2 10s ease-in-out infinite; }
                .animate-float-3 { animation: float-3 12s ease-in-out infinite; }

                @keyframes text-pulse {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                }
                .animate-text-pulse {
                    background-size: 200% 200%;
                    animation: text-pulse 0.5s ease 2;
                }

                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
                    20%, 40%, 60%, 80% { transform: translateX(5px); }
                }
                .animate-shake {
                    animation: shake 0.5s ease-in-out;
                }

                @keyframes button-click {
                    0% { transform: scale(1); }
                    50% { transform: scale(0.95); }
                    100% { transform: scale(1); }
                }
                .animate-button-click {
                    animation: button-click 0.3s ease-out;
                }

                @keyframes ping-once {
                    0% { transform: scale(0.8); opacity: 0.8; }
                    100% { transform: scale(2); opacity: 0; }
                }
                .animate-ping-once {
                    animation: ping-once 0.5s ease-out forwards;
                }
            `}</style>
        </section>
    );
}